import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Icon } from "@iconify/react";
import Common from "../../helper/common";
import { respEnum, ExternalSyncingLogTypeEnum } from "../../constants/appConstant";
import toast from "react-hot-toast";
import AdvanceTable from "../../components/advanceTable";
import { SearchTypeEnum } from "../../constants/appConstant";
import { AppSelect, AppInput } from "../../components/appComponents";

const ExternalSyncingLog = () => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncDate, setSyncDate] = useState("");
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterTillDate, setFilterTillDate] = useState("");

  useEffect(() => {
    getCredentials();
    getLogs();
  }, []);

  const getCredentials = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetExternalCreds",
        {},
        "GET",
        false
      );

      if (response?.status === respEnum.Success) {
        const creds = response.data && response.data.length > 0 ? response.data[0] : null;
        setCredentials(creds);
      }
    } catch (error) {
      console.error("Error fetching credentials:", error);
    }
  };

  const getLogs = async (
    pageNumber = 1,
    searchTerm = "",
    recordsToDisplay = null
  ) => {
    setLoading(true);
    try {
      const requestData = {
        pageNumber: pageNumber,
        q: searchTerm,
        recordsToDisplay,
      };

      if (filterType) {
        requestData.type = filterType;
      }

      if (filterFromDate) {
        requestData.fromDate = filterFromDate;
      }

      if (filterTillDate) {
        requestData.tillDate = filterTillDate;
      }

      const response = await Common.ApiService.getInstance().request(
        "GetExternalSyncingLogs",
        requestData,
        "POST"
      );

      if (response?.status === respEnum.Success && response?.data) {
        let obj = {
          ...response.data[0],
          data: response.data[0].data.map((item) => ({
            ...item,
            fromDate: Common.Utility.ConvertTime(item.fromDate),
            type: Common.Utility.getKeyByValue(ExternalSyncingLogTypeEnum, item.type) || item.type,
          })),
        };
        setData(obj);
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
      toast.error("Failed to load sync logs");
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!syncDate) {
      toast.error("Please select a date");
      return;
    }

    if (!credentials) {
      toast.error("Please configure credentials first. Go to External Credentials page.");
      return;
    }

    const selectedDate = new Date(syncDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate >= today) {
      toast.error("Cannot sync data for current date or future dates");
      return;
    }

    setSyncing(true);
    try {
      const response = await Common.ApiService.getInstance().request(
        "SyncHoHLogs",
        {
          date: syncDate,
        },
        "POST"
      );

      if (response?.status === respEnum.Success) {
        toast.success(response.message || "Data synced successfully");
        setSyncDialogOpen(false);
        setSyncDate("");
        getLogs(data?.pageNumber || 1);
      } else {
        toast.error(response.message || "Sync failed");
      }
    } catch (error) {
      console.error("Error syncing:", error);
      toast.error("Failed to sync data");
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return Common.moment(dateString).format("DD-MM-YYYY");
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    return Common.moment(dateString).format("DD-MM-YYYY HH:mm:ss");
  };

  useEffect(() => {
    getLogs(data?.pageNumber || 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, filterFromDate, filterTillDate]);

  const resetFilters = () => {
    setFilterType(null);
    setFilterFromDate("");
    setFilterTillDate("");
  };

  const typeFilterOptions = [
    { id: null, name: "All Types" },
    ...Common.Utility.enumToArray(ExternalSyncingLogTypeEnum),
  ];


  const _thead = [
    { title: "Id", type: "number", isVisible: false, key: "id" },
    { title: "Date", type: "text", isVisible: true, key: "fromDate" },
    { title: "Type", type: "text", isVisible: true, key: "type" },
    { title: "Status", type: "text", isVisible: true, key: "status" },
    { title: "Request", type: "text", isVisible: true, key: "request" },
    { title: "Response", type: "text", isVisible: true, key: "response" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-xl font-semibold">External ERP Sync Logs</CardTitle>
          <Dialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="primary"
                disabled={!credentials}
                className="flex items-center gap-2"
              >
                <Icon icon="heroicons:arrow-path" className="h-4 w-4" />
                Sync Now
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Sync Data to External ERP</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Select Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={syncDate}
                    onChange={(e) => setSyncDate(e.target.value)}
                    max={Common.moment().subtract(1, "day").format("YYYY-MM-DD")}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Cannot sync data for current date or future dates
                  </p>
                  {!credentials && (
                    <p className="text-xs text-red-500 mt-2">
                      Please configure credentials first in External Credentials page
                    </p>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSyncDialogOpen(false);
                    setSyncDate("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSync}
                  disabled={syncing || !syncDate || !credentials}
                >
                  {syncing ? (
                    <>
                      <Icon icon="heroicons:arrow-path" className="h-4 w-4 mr-2 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    "Sync"
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <div className="mb-4 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <AppSelect
              value={filterType}
              onChange={setFilterType}
              label="Filter by Type"
              data={typeFilterOptions}
              className="w-full"
            />
            <AppInput
              type="date"
              value={filterFromDate}
              onChange={(e) => setFilterFromDate(e.target.value)}
              label="From Date"
              className="w-full"
            />
            <AppInput
              type="date"
              value={filterTillDate}
              onChange={(e) => setFilterTillDate(e.target.value)}
              label="Till Date"
              className="w-full"
            />
            {(filterType || filterFromDate || filterTillDate) && (
              <Button
                variant="outline"
                onClick={resetFilters}
                className="h-10"
              >
                Reset Filters
              </Button>
            )}
          </div>
          <AdvanceTable
            title="Sync History"
            columns={_thead.filter((item) => item.isVisible).map(col => {
              if (col.key === "fromDate") {
                return { ...col, formatter: (val) => formatDate(val) };
              }
              if (col.key === "status") {
                return {
                  ...col, formatter: (val) => {
                    const statusLower = val?.toLowerCase() || "";
                    if (statusLower.includes("success")) return "Success";
                    if (statusLower.includes("failed") || statusLower.includes("error")) return "Failed";
                    if (statusLower.includes("progress")) return "In Progress";
                    return val || "Unknown";
                  }
                };
              }
              if (col.key === "request" || col.key === "response") {
                return {
                  ...col, formatter: (val) => {
                    if (!val) return "-";
                    // Try to format as JSON if possible
                    try {
                      const parsed = typeof val === 'string' ? JSON.parse(val) : val;
                      const formatted = JSON.stringify(parsed, null, 2);
                      return formatted.length > 100 ? formatted.substring(0, 100) + "..." : formatted;
                    } catch {
                      return val.length > 100 ? val.substring(0, 100) + "..." : val;
                    }
                  }
                };
              }
              return col;
            })}
            data={data}
            getData={getLogs}
            showPagination={true}
            loading={loading}
            search={SearchTypeEnum.Local_Search}
            searchInputClassName="custom-search-mobile"
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default ExternalSyncingLog;

